// src/components/media/ImagesTab.tsx — the Images tab on a building's page (its Building photos) and on a flat's page
// (Photos / Floorplans / EPC): the Media page's grid, opened on that building's or flat's own folder.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { EmptyState, Segmented, Skeleton } from '../ui';
import { MediaGrid } from './MediaGrid';
import { indexTree, mediaApi, type FolderKind, type MediaFolder } from '../../lib/media';

const FLAT_TABS: Array<{ key: FolderKind; label: string }> = [
  { key: 'UNIT_PHOTOS', label: 'Photos' }, { key: 'UNIT_FLOORPLANS', label: 'Floorplans' }, { key: 'UNIT_EPC', label: 'EPC' },
];

export function ImagesTab(props: { propertyId: string; unitId?: undefined } | { unitId: string; propertyId?: string }) {
  const flat = !!props.unitId;
  const tree = useQuery({
    queryKey: ['media', 'tree', flat ? { unitId: props.unitId } : { propertyId: props.propertyId }],
    queryFn: () => mediaApi.tree(flat ? { unitId: props.unitId } : { propertyId: props.propertyId }),
  });
  const [kind, setKind] = useState<FolderKind>(flat ? 'UNIT_PHOTOS' : 'PROPERTY_GALLERY');
  const byKind = useMemo(() => {
    const m = new Map<FolderKind, MediaFolder>();
    for (const f of indexTree(tree.data ?? []).values()) {
      // A building's tree holds its flats' folders too: only the building's own gallery counts here.
      if (flat ? f.unitId === props.unitId : !f.unitId) if (!m.has(f.kind)) m.set(f.kind, f);
    }
    return m;
  }, [tree.data, flat, props.unitId]);
  const folder = byKind.get(kind);
  const home = byKind.get(flat ? 'UNIT' : 'PROPERTY');

  if (tree.isLoading) return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />)}</div>;
  if (tree.isError) return <EmptyState icon="AlertTriangle" title="The images could not be loaded" body={(tree.error as Error)?.message} />;
  if (!folder) return <EmptyState icon="Images" title="No image folder" body={`This ${flat ? 'flat' : 'building'} has no folder in Media (an archived one is kept there, hidden).`} />;

  const header = (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      {flat
        ? <Segmented value={kind} onChange={setKind} items={FLAT_TABS.filter((t) => byKind.has(t.key)).map((t) => ({ key: t.key, label: t.label, count: byKind.get(t.key)?.itemCount || null }))} />
        : <p className="text-[13px] font-semibold text-ink-900">Building photos <span className="font-normal text-ink-500">— the lobby, the gym, the outside: the building's gallery on the website</span></p>}
      {home && <Link to={`/app/marketing/media?folder=${folder.id}`} className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline">Open in Media<ExternalLink className="h-3 w-3" /></Link>}
    </div>
  );
  return (
    <MediaGrid folder={{ id: folder.id, name: folder.name, kind: folder.kind, isAuto: folder.isAuto, archived: folder.archived }} header={header}
      emptyHint={kind === 'UNIT_PHOTOS' ? 'Drag the flat’s photos in from your computer. The first one is the cover on the website.' : kind === 'PROPERTY_GALLERY' ? 'Drag in photos of the lobby, the gym, the outside…' : undefined} />
  );
}
